<script>
  import { _ } from "svelte-i18n"
  import { user, modals, can_upgrade_from_pro_to_business, user_subscription, business_subscription_deactivated, user_uploaded_fonts, user_uploaded_fonts_loading_state } from '../stores'
  import { loadUserContentData } from '../services/user'
  import OpenBuyModalButton from '../components/OpenBuyModalButton.svelte'
  import Insert from '../elements/Insert.svelte'
  import Dropzone from '../components/Dropzone.svelte'
  import Button from '../elements/Button.svelte'
  import Blink from '../elements/Blink.svelte'
  import EmptyStateFooterList from './EmptyStateFooterList.svelte'
  import BusinessUpsellNotice from '../components/BusinessUpsellNotice.svelte'

  function openUpgradeToBusinessModal() {
    modals.open('upgrade_to_business')
    if (typeof window.gtag == "undefined") return
    gtag('event', 'upgrade_to_business_modal_shown', {
      'initiator': 'storefront_actions'
    });
  }

  function openSubscribeModal() {
    modals.open('buy')
    if (typeof window.gtag == "undefined") return
    gtag('event', 'buy_modal_shown', {
      'initiator': 'uploaded_fonts_empty_state'
    });
  }

  const handleReloadFonts = () => {
    // show loading state
    user_uploaded_fonts_loading_state.setLoading()
    // wait 1 second to show that actually trying to load fonts
    setTimeout(() => {
      // Need to reload all user content data to be applied to uploaded fonts
      loadUserContentData()
    }, 1000)
  }

</script>

<div class="content">
  {#if $user === undefined}
    <Insert persistent insert_position="before_previews">
      <span slot="toptitle">
        BUSINESS PLAN FEATURE: UPLOAD AND PREVIEW ANYWHERE
      </span>
      <span slot="title">
        Upload your fonts into your account,<br>
        access them anywhere, anytime!
      </span>
      <span slot="text">
        Have a curated collection of fonts that you need to access everywhere?
        Now you can upload them to your account and access them on any browser or device, including your tablet.
        <small>
          This feature is available in the Wordmark Business plan along with the Storefronts feature. 
        </small>
      </span>
      <span slot="button">
        <OpenBuyModalButton feature='taged_fonts_page'>Subscribe to <b>Wordmark Business</b></OpenBuyModalButton>
        <Button kind="outline" size="small" href="/help/uploaded-fonts/" target="_blank">↗︎ Learn more about uploaded fonts</Button>
      </span>
    </Insert>
    <EmptyStateFooterList classList="ml-1">
      <li><small><b>Uploaded fonts are private and only accessible from your account.</b></small></li>
      <li><small>Uploaded fonts can also be used in your storefronts.</small></li>
      <li><small><a href="/help/uploaded-fonts/" target="_blank">Read more about this feature</a> in our help pages.</small></li>
    </EmptyStateFooterList>
  {:else if $user_uploaded_fonts_loading_state.loading}
    <div class="loading">
      <Blink>Loading uploaded fonts...</Blink>
    </div>
  {:else if $user_uploaded_fonts_loading_state.error}
    <div class="error">
      <p>There was an error loading your uploaded fonts ({$user_uploaded_fonts_loading_state.error}).</p>
      <Button kind="accent" on:click={handleReloadFonts}>Retry loading</Button>
    </div>
  {:else}
    <div class="body">
      <h2>
        Upload your fonts to your account,<br>
        <span>access them from any browser on any device</span>
      </h2>
      <p>
        This feature is useful for accessing your fonts across different devices and browsers.<br>
        It’s especially helpful on tablets and other devices and browsers that don’t support local fonts.
      </p>
      {#if $can_upgrade_from_pro_to_business}
        <p>
          <b>To try it out</b> <span class="arrow">⇢</span> <span class="action">Select</span> the fonts for upload <span class="arrow">⇢</span> <span class="action">Filter</span> selected fonts <span class="arrow">⇢</span> click the <span class="action">Upload</span> button on top.<br>
        </p>
        <BusinessUpsellNotice in_page={true}/>
      {:else if $business_subscription_deactivated}
        <div class="button-group">
          Your business subscription is deactivated, 
          <Button kind="accent-orange" on_fill={true} on:click={openSubscribeModal}>
            {$_('buttons.subscribeTo')} <b>{$_('app.businessName')}</b>
          </Button>
        </div>
      {:else}
        <p>
          <b>To get started</b> <span class="arrow">⇢</span> <span class="action">Select</span> the fonts for upload <span class="arrow">⇢</span> <span class="action">Filter</span> selected fonts <span class="arrow">⇢</span> click the <span class="action">Upload</span> button on top.<br>
        </p>
        <p class="mt-1">
          You can also drop some font files here <Dropzone inputOnly/>
        </p>
      {/if}
    </div>
    <EmptyStateFooterList>
      <li><small><b>Uploaded fonts are private and only accessible from your account.</b></small></li>
      <li><small>You can delete uploaded fonts under your profile menu on top right.</small></li>
      <li><small>Uploaded fonts can also be used in your storefronts.</small></li>
      <li><small><a href="/help/uploaded-fonts/" target="_blank">Read more about this feature</a> in our help pages.</small></li>
    </EmptyStateFooterList>
  {/if}
</div>

<style>
  .content {
    min-height: calc(100vh - 16rem);
  }
  .body {
    padding: 2rem 1rem 3rem;
  }
  .body h2 {
    font-size: 2.2rem;
    margin-bottom: 2.5rem;
  }
  .body h2 span {
    color: var(--c-text);
  }
  .body p {
    margin-top: 3rem;
  }
  .button-group {
    margin-top: 3rem;
  }
  .mt-1 {
    margin-top: 1rem !important;
  }
  .action {
    color: var(--c-accent);
    font-family: var(--PensumSans-Bold);
  }
  .arrow {
    font-size: 1.5rem;
    vertical-align: bottom;
    margin: 0 0.25rem;
  }
  .loading {
    padding: 1rem;
  }
  .error {
    padding: 1rem;
  }
  .error p {
    color: var(--c-danger);
    margin-bottom: 2rem;
  }
</style>